import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import ScrollToTop from "./components/ScrollToTop";
import api, { isPlatformHostname } from "./lib/api";
import SitePreloader from "@/components/SitePreloader";
import { normalizePreloader } from "@/lib/preloaders";
import { bootStore, readHostCache, writeHostCache } from "@/lib/storeBoot";

// Providers
import { MerchantAuthProvider, PlatformAuthProvider, SalesAuthProvider, useMerchantAuth, usePlatformAuth, useSalesAuth } from "./lib/auth";
import { UploadProgressProvider } from "@/lib/uploadProgress";

// Marketing
import Landing from "./pages/Landing";
import FeaturesPage from "./pages/FeaturesPage";
import LandingClassic from "./pages/LandingClassic";
import ThemeMarketplace from "./pages/ThemeMarketplace";
import GadsLanding from "./pages/GadsLanding";
import DomainPage from "./pages/DomainPage";
import DomainUnconnected from "./pages/DomainUnconnected";

// Merchant
import MerchantSignup from "./pages/merchant/MerchantSignup";
import MerchantThemeSetup from "./pages/merchant/MerchantThemeSetup";
import MerchantLogin from "./pages/merchant/MerchantLogin";
import MerchantForgotPassword from "./pages/merchant/MerchantForgotPassword";
import MerchantResetPassword from "./pages/merchant/MerchantResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import AdminLayout from "./pages/merchant/AdminLayout";
import AdminDashboard from "./pages/merchant/AdminDashboard";
import AdminProducts from "./pages/merchant/AdminProducts";
import AdminProductForm from "./pages/merchant/AdminProductForm";
import AdminCategories from "./pages/merchant/AdminCategories";
import AdminOrders from "./pages/merchant/AdminOrders";
import AdminOrderDetail from "./pages/merchant/AdminOrderDetail";
import AdminCustomers from "./pages/merchant/AdminCustomers";
import AdminSettings from "./pages/merchant/AdminSettings";
import AdminAppearance from "./pages/merchant/AdminAppearance";
import AdminPages from "./pages/merchant/AdminPages";
import AdminSeo from "./pages/merchant/AdminSeo";
import AdminMedia from "./pages/merchant/AdminMedia";
import AdminCoupons from "./pages/merchant/AdminCoupons";
import AdminSupport from "./pages/merchant/AdminSupport";
import MyApplication from "./pages/merchant/MyApplication";
import AdminBilling from "./pages/merchant/AdminBilling";
import AdminAddons from "./pages/merchant/AdminAddons";
import AdminFortuneWheel from "./pages/merchant/AdminFortuneWheel";
import AdminInvoiceMaker from "./pages/merchant/AdminInvoiceMaker";
import AdminSingleProduct from "./pages/merchant/AdminSingleProduct";
import AdminCustomerRefer from "./pages/merchant/AdminCustomerRefer";
import AdminCustomForm from "./pages/merchant/AdminCustomForm";
import AdminMultiCurrency from "./pages/merchant/AdminMultiCurrency";
import AdminPartialPayments from "./pages/merchant/AdminPartialPayments";
import AdminCopyrightRemoval from "./pages/merchant/AdminCopyrightRemoval";
import AdminFirebaseOtp from "./pages/merchant/AdminFirebaseOtp";
import AdminGokwikCheckout from "./pages/merchant/AdminGokwikCheckout";
import AdminFastrrCheckout from "./pages/merchant/AdminFastrrCheckout";
import AdminTimeslotCheckout from "./pages/merchant/AdminTimeslotCheckout";
import AdminAIStoreManager from "./pages/merchant/AdminAIStoreManager";
import AdminOndemandAreas from "./pages/merchant/AdminOndemandAreas";
import AdminOndemandRiders from "./pages/merchant/AdminOndemandRiders";
import AdminBuildBoxes from "./pages/merchant/AdminBuildBoxes";
import AdminAbp from "./pages/merchant/AdminAbp";
import AdminContactForm from "./pages/merchant/AdminContactForm";
import AdminCoursePages from "./pages/merchant/AdminCoursePages";
import AdminAuthors from "./pages/merchant/AdminAuthors";
import AdminBuildBoxForm from "./pages/merchant/AdminBuildBoxForm";
import DeliverLogin from "./pages/deliver/DeliverLogin";
import DeliverPanel from "./pages/deliver/DeliverPanel";
import AdminStoreBlogs from "./pages/merchant/AdminStoreBlogs";
import AdminStoreBlogForm from "./pages/merchant/AdminStoreBlogForm";
import AdminAds from "./pages/merchant/AdminAds";
import MerchantWallet from "./pages/merchant/MerchantWallet";
import AdminDomain from "./pages/merchant/AdminDomain";

// Platform
import SuperLogin from "./pages/super/SuperLogin";
import SuperLayout from "./pages/super/SuperLayout";
import SuperDashboard from "./pages/super/SuperDashboard";
import SuperTenants from "./pages/super/SuperTenants";
import SuperIntegrations from "./pages/super/SuperIntegrations";
import SuperAudit from "./pages/super/SuperAudit";
import SuperSupport from "./pages/super/SuperSupport";
import SuperSupportAi from "./pages/super/SuperSupportAi";
import SuperLanding from "./pages/super/SuperLanding";
import SuperCoupons from "./pages/super/SuperCoupons";
import SuperLeads from "./pages/super/SuperLeads";
import SuperSeo from "./pages/super/SuperSeo";
import SuperBlog from "./pages/super/SuperBlog";
import SuperMediaMigrate from "./pages/super/SuperMediaMigrate";
import SuperAddons from "./pages/super/SuperAddons";
import SuperBulkAddons from "./pages/super/SuperBulkAddons";
import SuperSalesUsers from "./pages/super/SuperSalesUsers";
import SuperSalesTargets from "./pages/super/SuperSalesTargets";
import SuperFacebook from "./pages/super/SuperFacebook";
import SuperGads from "./pages/super/SuperGads";
import SuperPurchases from "./pages/super/SuperPurchases";
import SuperAbandonedCarts from "./pages/super/SuperAbandonedCarts";
import SuperFbDeletion from "./pages/super/SuperFbDeletion";
import SuperTranslation from "./pages/super/SuperTranslation";
import SuperWallet from "./pages/super/SuperWallet";
import SuperThemeSettings from "./pages/super/SuperThemeSettings";
import SuperAdultControl from "./pages/super/SuperAdultControl";

import SalesLogin from "./pages/sales/SalesLogin";
import SalesLayout from "./pages/sales/SalesLayout";
import SalesNotices from "./pages/sales/SalesNotices";
import SalesPush from "./pages/sales/SalesPush";
import SalesCrm from "./pages/sales/SalesCrm";
import SalesWhatsApp from "./pages/sales/SalesWhatsApp";
import SalesInvoice from "./pages/sales/SalesInvoice";
import SalesAppLive from "./pages/sales/SalesAppLive";
import PublicInvoice from "./pages/sales/PublicInvoice";
import InvoicePublic from "./pages/InvoicePublic";

// Public policy / legal pages
import PolicyPage from "./pages/PolicyPage";
import CompanyPersonPage from "./pages/CompanyPersonPage";
import FbDataDeletion from "./pages/FbDataDeletion";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import BlogTopic from "./pages/BlogTopic";
import CityPage from "./pages/CityPage";

// Storefront
import StorefrontShell from "./pages/storefront/StorefrontShell";

function ProtectedMerchant({ children }) {
  const { loading, user, tenant } = useMerchantAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const needsSetup = tenant?.needs_theme_setup === true && tenant?.store_enabled !== false;
  const onSetup = loc.pathname === "/admin/setup-theme";
  if (needsSetup && !onSetup) return <Navigate to="/admin/setup-theme" replace />;
  if (!needsSetup && onSetup) return <Navigate to="/admin" replace />;
  return children;
}
function ProtectedPlatform({ children }) {
  const { loading, user } = usePlatformAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/super-admin/login" replace />;
  return children;
}
function ProtectedSales({ children }) {
  const { loading, user } = useSalesAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/sales/login" replace />;
  return children;
}

function App() {
  // ---- Custom-domain detection ----
  // If the app is served on a merchant's own domain, render that store at the
  // root (clean URLs). Platform/preview hosts skip the lookup for instant paint.
  const search = new URLSearchParams(window.location.search);
  const overrideHost = search.get("__host"); // test/preview hook
  const host = (overrideHost || window.location.hostname || "").toLowerCase();
  const isPlatformHost = !overrideHost && isPlatformHostname(host);
  const boot = !isPlatformHost ? bootStore() : null;
  const cachedHost = !isPlatformHost ? readHostCache(host) : null;
  const known = boot?.slug ? boot : cachedHost;
  const [cd, setCd] = useState({
    checked: isPlatformHost || Boolean(known?.slug),
    slug: known?.slug || null,
    preloader: normalizePreloader(known?.preloader),
  });

  useEffect(() => {
    if (isPlatformHost) return undefined;
    let alive = true;
    api.get(`/resolve_host?host=${encodeURIComponent(host)}`)
      .then(({ data }) => {
        if (!alive) return;
        const slug = data?.tenant?.slug || null;
        const preloader = normalizePreloader(data?.tenant?.preloader);
        writeHostCache(host, slug, preloader);
        setCd({ checked: true, slug, preloader });
      })
      .catch(() => { if (alive) setCd((prev) => ({ ...prev, checked: true })); });
    return () => { alive = false; };
  }, [host, isPlatformHost]);

  if (!cd.checked) {
    return <SitePreloader id={cd.preloader} testId="store-host-loading" />;
  }

  const customStore = cd.slug; // non-null => serve this store at the root
  return (
    <div className="App">
      <HelmetProvider>
        <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <ScrollToTop />
        <UploadProgressProvider>
        <MerchantAuthProvider>
          <PlatformAuthProvider>
            <SalesAuthProvider>
            <Routes>
              {customStore ? (
                <>
                  {/* Custom domain: the entire site is THIS merchant's storefront.
                      No platform pages are mounted here, so an accidental
                      /about can't load the platform landing page. Reserved
                      platform paths redirect into the storefront's own page so
                      /about -> /page/about (falls to storefront 404/home if the
                      merchant has no such page). */}
                  <Route path="/bill/:token" element={<InvoicePublic storeSlug={customStore} />} />
                  {["about", "contact", "privacy-policy", "terms", "return-policy", "disclaimer", "shipping-policy", "fb-data-deletion", "blog", "cities", "theme", "themes", "company", "features", "pricing"].map((p) => (
                    <Route key={p} path={`/${p}`} element={<Navigate to={`/page/${p}`} replace />} />
                  ))}
                  <Route path="/*" element={<StorefrontShell forcedSlug={customStore} hintPreloader={cd.preloader} />} />
                </>
              ) : !isPlatformHost ? (
                <Route path="/*" element={<DomainUnconnected />} />
              ) : (
                <>
                  <Route path="/" element={<Landing />} />
                  <Route path="/pricing" element={<Landing />} />
                  <Route path="/features" element={<FeaturesPage />} />
                  <Route path="/theme" element={<ThemeMarketplace />} />
                  <Route path="/themes" element={<Navigate to="/theme" replace />} />
                  <Route path="/landing-classic" element={<LandingClassic />} />
                  <Route path="/india-shopify-alternative" element={<GadsLanding />} />
                  <Route path="/domain" element={<DomainPage />} />

                  {/* Public policy / legal pages (reserved slugs) */}
                  <Route path="/about" element={<PolicyPage doc="about" />} />
                  <Route path="/company/founder" element={<CompanyPersonPage role="founder" />} />
                  <Route path="/company/co-founder" element={<CompanyPersonPage role="co-founder" />} />
                  <Route path="/contact" element={<PolicyPage doc="contact" />} />
                  <Route path="/privacy-policy" element={<PolicyPage doc="privacy" />} />
                  <Route path="/terms" element={<PolicyPage doc="terms" />} />
                  <Route path="/return-policy" element={<PolicyPage doc="return" />} />
                  <Route path="/disclaimer" element={<PolicyPage doc="disclaimer" />} />
                  <Route path="/shipping-policy" element={<PolicyPage doc="shipping" />} />
                  <Route path="/invoice/:token" element={<PublicInvoice />} />
                  <Route path="/bill/:token" element={<InvoicePublic />} />
                  <Route path="/:slug/bill/:token" element={<InvoicePublic />} />
                  <Route path="/blog" element={<BlogIndex />} />
                  <Route path="/blog/topics/:cluster" element={<BlogTopic />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/cities/:slug" element={<CityPage />} />

                  <Route path="/deliver" element={<DeliverLogin />} />
                  <Route path="/deliver/orders" element={<DeliverPanel />} />

                  <Route path="/signup" element={<MerchantSignup />} />
                  <Route path="/login" element={<MerchantLogin />} />
                  <Route path="/forgot-password" element={<MerchantForgotPassword />} />
                  <Route path="/reset-password" element={<MerchantResetPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />

                  <Route path="/admin/setup-theme" element={<ProtectedMerchant><MerchantThemeSetup /></ProtectedMerchant>} />
                  <Route path="/admin" element={<ProtectedMerchant><AdminLayout /></ProtectedMerchant>}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="products/new" element={<AdminProductForm />} />
                    <Route path="products/:id" element={<AdminProductForm />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="orders/:id" element={<AdminOrderDetail />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="coupons" element={<AdminCoupons />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="appearance" element={<AdminAppearance />} />
                    <Route path="pages" element={<AdminPages />} />
                    <Route path="blogs" element={<AdminStoreBlogs />} />
                    <Route path="blogs/new" element={<AdminStoreBlogForm />} />
                    <Route path="blogs/:id" element={<AdminStoreBlogForm />} />
                    <Route path="seo" element={<AdminSeo />} />
                    <Route path="media" element={<AdminMedia />} />
                    <Route path="support" element={<AdminSupport />} />
                    <Route path="my-application" element={<MyApplication />} />
                    <Route path="addons" element={<AdminAddons />} />
                    <Route path="addons/fortune-wheel" element={<AdminFortuneWheel />} />
                    <Route path="addons/invoice-maker" element={<AdminInvoiceMaker />} />
                    <Route path="addons/single-product" element={<AdminSingleProduct />} />
                    <Route path="addons/refer-earn" element={<AdminCustomerRefer />} />
                    <Route path="addons/custom-form" element={<AdminCustomForm />} />
                    <Route path="addons/multi-currency" element={<AdminMultiCurrency />} />
                    <Route path="addons/partial-payments" element={<AdminPartialPayments />} />
                    <Route path="addons/copyright-removal" element={<AdminCopyrightRemoval />} />
                    <Route path="addons/firebase-otp" element={<AdminFirebaseOtp />} />
                    <Route path="addons/gokwik-checkout" element={<AdminGokwikCheckout />} />
                    <Route path="addons/fastrr-checkout" element={<AdminFastrrCheckout />} />
                    <Route path="addons/timeslot-checkout" element={<AdminTimeslotCheckout />} />
                    <Route path="addons/ai-store-manager" element={<AdminAIStoreManager />} />
                    <Route path="ondemand/areas" element={<AdminOndemandAreas />} />
                    <Route path="ondemand/riders" element={<AdminOndemandRiders />} />
                    <Route path="build-boxes" element={<AdminBuildBoxes />} />
                    <Route path="abp" element={<AdminAbp />} />
                    <Route path="contact-form" element={<AdminContactForm />} />
                    <Route path="course-pages" element={<AdminCoursePages />} />
                    <Route path="authors" element={<AdminAuthors />} />
                    <Route path="build-boxes/new" element={<AdminBuildBoxForm />} />
                    <Route path="build-boxes/:id" element={<AdminBuildBoxForm />} />
                    <Route path="ads" element={<AdminAds />} />
                    <Route path="billing" element={<AdminBilling />} />
                    <Route path="domain" element={<AdminDomain />} />
                    <Route path="refer" element={<MerchantWallet />} />
                  </Route>

                  <Route path="/super-admin/login" element={<SuperLogin />} />
                  <Route path="/super-admin" element={<ProtectedPlatform><SuperLayout /></ProtectedPlatform>}>
                    <Route index element={<SuperDashboard />} />
                    <Route path="tenants" element={<SuperTenants />} />
                    <Route path="theme-settings" element={<SuperThemeSettings />} />
                    <Route path="adult-control" element={<SuperAdultControl />} />
                    <Route path="addons" element={<SuperAddons />} />
                    <Route path="bulk-addons" element={<SuperBulkAddons />} />
                    <Route path="integrations" element={<SuperIntegrations />} />
                    <Route path="media" element={<SuperMediaMigrate />} />
                    <Route path="landing" element={<SuperLanding />} />
                    <Route path="seo" element={<SuperSeo />} />
                    <Route path="blog" element={<SuperBlog />} />
                    <Route path="translation" element={<SuperTranslation />} />
                    <Route path="wallet" element={<SuperWallet />} />
                    <Route path="coupons" element={<SuperCoupons />} />
                    <Route path="leads" element={<SuperLeads />} />
                    <Route path="audit" element={<SuperAudit />} />
                    <Route path="support" element={<SuperSupport />} />
                    <Route path="support-ai" element={<SuperSupportAi />} />
                    <Route path="sales-users" element={<SuperSalesUsers />} />
                    <Route path="sales-targets" element={<SuperSalesTargets />} />
                    <Route path="facebook" element={<SuperFacebook />} />
                    <Route path="google-ads" element={<SuperGads />} />
                    <Route path="purchase-billing" element={<SuperPurchases />} />
                    <Route path="abandoned-cart" element={<SuperAbandonedCarts />} />
                    <Route path="fb-deletion" element={<SuperFbDeletion />} />
                  </Route>

                  <Route path="/sales/login" element={<SalesLogin />} />
                  <Route path="/sales" element={<ProtectedSales><SalesLayout /></ProtectedSales>}>
                    <Route index element={<SuperDashboard />} />
                    <Route path="tenants" element={<SuperTenants restricted />} />
                    <Route path="theme-settings" element={<SuperThemeSettings />} />
                    <Route path="adult-control" element={<SuperAdultControl />} />
                    <Route path="purchase-billing" element={<SuperPurchases />} />
                    <Route path="abandoned-cart" element={<SuperAbandonedCarts />} />
                    <Route path="app-live" element={<SalesAppLive />} />
                    <Route path="addons" element={<SuperAddons />} />
                    <Route path="bulk-addons" element={<SuperBulkAddons />} />
                    <Route path="wallet" element={<SuperWallet />} />
                    <Route path="notices" element={<SalesNotices />} />
                    <Route path="push" element={<SalesPush />} />
                    <Route path="crm" element={<SalesCrm />} />
                    <Route path="whatsapp" element={<SalesWhatsApp />} />
                    <Route path="invoices" element={<SalesInvoice />} />
                    <Route path="invoices/:id" element={<SalesInvoice />} />
                    <Route path="support" element={<SuperSupport />} />
                    <Route path="coupons" element={<SuperCoupons />} />
                    <Route path="leads" element={<SuperLeads />} />
                  </Route>

                  {/* Storefront by slug (must come last) */}
                  <Route path="/:slug/*" element={<StorefrontShell />} />
                </>
              )}
            </Routes>
            </SalesAuthProvider>
          </PlatformAuthProvider>
        </MerchantAuthProvider>
        </UploadProgressProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
